import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { PolicyPage, Section, Bullets, MERCHANT } from './legal';

const PLANS = [
  {
    key: 'starter', name: 'Starter', monthly: 1499, yearly: 14990,
    includes: ['Customers & vehicle records', 'Estimates and invoices', 'PDF + WhatsApp sharing', 'Up to 5 staff logins'],
  },
  {
    key: 'professional', name: 'Professional', monthly: 3999, yearly: 39990, featured: true,
    includes: ['Everything in Starter', 'Vehicle inspections with photos', 'Inventory & consumption tracking',
      'eWarranty certificates', 'Payroll & attendance', 'GST invoicing & returns', 'Up to 25 staff logins'],
  },
  {
    key: 'enterprise', name: 'Enterprise', monthly: 7999, yearly: 79990,
    includes: ['Everything in Professional', 'Performance reviews & skill matrix', 'Drive IQ training plans & team analysis',
      'SOP training modules', 'Multi-branch books', 'Unlimited staff logins'],
  },
];

const inr = (n) => `₹${n.toLocaleString('en-IN')}`;

const DrivelinePricing = () => {
  const [cycle, setCycle] = useState('monthly');
  const yearly = cycle === 'yearly';

  return (
  <PolicyPage
    testid="driveline-pricing"
    title="Pricing"
    intro="Driveline is sold as a monthly or annual software subscription. Every price on this page is
           in Indian Rupees and applies to one workshop account."
  >
    <div className="mt-10 flex flex-col items-center gap-2">
      <div className="inline-flex p-1 rounded-full bg-slate-100 border border-slate-200" data-testid="pricing-toggle">
        {[
          { key: 'monthly', label: 'Monthly' },
          { key: 'yearly', label: 'Yearly' },
        ].map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => setCycle(o.key)}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
              cycle === o.key ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-900'}`}
            data-testid={`pricing-toggle-${o.key}`}
          >
            {o.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-teal-700 font-medium" data-testid="pricing-toggle-hint">
        {yearly ? 'Two months free — you pay for 10, you get 12' : 'Switch to yearly and two months are free'}
      </p>
    </div>

    <div className="mt-6 grid lg:grid-cols-3 gap-5" data-testid="pricing-cards">
      {PLANS.map((p) => (
        <div
          key={p.key}
          className={`rounded-2xl border bg-white p-6 ${p.featured ? 'border-slate-900' : 'border-slate-200'}`}
          data-testid={`pricing-card-${p.key}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-slate-900">{p.name}</span>
            {p.featured && (
              <span className="text-[10px] uppercase tracking-wider bg-red-500/10 text-red-600 px-2 py-0.5 rounded-full">
                Most popular
              </span>
            )}
          </div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="text-3xl font-bold tabular-nums text-slate-900" data-testid={`pricing-amount-${p.key}`}>
              {inr(yearly ? p.yearly : p.monthly)}
            </span>
            <span className="text-sm text-slate-400">{yearly ? '/year' : '/month'}</span>
          </div>
          <p className="mt-1 text-sm text-slate-500" data-testid={`pricing-alt-${p.key}`}>
            {yearly
              ? `${inr(p.monthly * 12)} if paid monthly — you save ${inr(p.monthly * 12 - p.yearly)}`
              : `or ${inr(p.yearly)}/year — two months free`}
          </p>
          <p className="mt-0.5 text-xs text-slate-400">+ 18% GST</p>
          <ul className="mt-4 space-y-1.5">
            {p.includes.map((t) => (
              <li key={t} className="flex gap-2 text-sm text-slate-600">
                <Check size={15} className="text-teal-600 shrink-0 mt-0.5" /> {t}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>

    <Section title="Drive IQ is included" testid="pricing-driveiq">
      <p>
        <b>Drive IQ</b> — know your business inside out — is the intelligence built into
        Driveline, and it is part of your plan rather than an add-on priced per question.
      </p>
      <Bullets items={[
        'Every plan: ask Drive IQ anything about how Driveline works, or about your own numbers, from the help panel on any screen.',
        'Professional and above: photograph a purchase bill and Drive IQ reads the vendor, amount, date and GST straight into the expense entry.',
        'Enterprise: training plans drafted from a technician\u2019s skill assessment, a plain-words summary of your team\u2019s situation, and SOPs translated into your floor\u2019s own language.',
      ]} />
      <p>
        Drive IQ reads only your workshop&apos;s data, only what the signed-in person is permitted
        to see, and never writes to your records. It is not trained on your data and it never
        has sight of another workshop&apos;s numbers.
      </p>
    </Section>

    <Section title="Free trial" testid="pricing-trial">
      <p>
        Every new workshop account begins with a <b>14-day free trial</b> of the plan it signs up on.
        No payment details are collected to start the trial and nothing is charged during it. If you do
        not subscribe by the end of the trial, the account becomes read-only — you can still view and
        download everything you created, but you cannot add new records until a subscription starts.
      </p>
    </Section>

    <Section title="Taxes" testid="pricing-taxes">
      <p>
        All plan prices listed above are <b>exclusive of GST</b>. Goods and Services Tax is charged at
        the prevailing rate (currently 18%) and shown separately on your tax invoice. For example,
        Professional monthly is billed at {inr(3999)} + {inr(720)} GST = <b>{inr(4719)}</b>.
      </p>
      <p>
        A GST tax invoice is issued for every successful payment and emailed to the billing contact on
        the account. Add your GSTIN in your account settings before the first charge if you intend to
        claim input tax credit.
      </p>
    </Section>

    <Section title="Billing cycle and renewal" testid="pricing-cycle">
      <Bullets items={[
        'No card, mandate or auto-debit is needed for the 14-day free trial.',
        'When the trial ends, we send you a payment link for your plan by WhatsApp or email.',
        'Monthly plans are billed each month and annual plans each year, with a fresh payment link before each renewal.',
        'Plan changes after payment are arranged with our team.',
      ]} />
    </Section>

    <Section title="How you pay" testid="pricing-methods">
      <p>
        Payment links are processed by <b>Cashfree Payments</b>, a PCI-DSS compliant payment gateway
        authorised by the Reserve Bank of India. {MERCHANT.legal} never sees or stores your card,
        UPI or bank credentials. You can pay by UPI, card or net banking.
      </p>
    </Section>

    <Section title="Unpaid accounts" testid="pricing-failures">
      <p>
        Until your payment link is paid, the account is read-only: your data stays exactly where it
        is and remains viewable and downloadable. Full access is restored as soon as we confirm
        your payment.
      </p>
    </Section>

    <Section title="What is not charged for" testid="pricing-inclusions">
      <Bullets items={[
        'No setup, onboarding or migration fee.',
        'No per-invoice, per-estimate or per-document charge.',
        'No charge for software updates or new features released on your plan.',
        'Email support during business hours is included on every plan.',
      ]} />
      <p>
        Charges for optional work outside the subscription — for example bulk data migration from
        another system, or custom report development — are quoted and approved in writing before any
        work begins.
      </p>
    </Section>

    <Section title="Changes to pricing" testid="pricing-changes">
      <p>
        We may revise plan pricing. Existing subscribers are given at least 30 days' notice by email
        before a revised price applies to their renewal, and may cancel before that renewal if they do
        not wish to continue. Prices are never changed mid-cycle.
      </p>
    </Section>

    <p className="mt-10 text-sm text-slate-600">
      See also:{' '}
      <Link to="/driveline/refund-policy" className="text-teal-700 underline" data-testid="pricing-to-refund">
        Refund &amp; Cancellation Policy
      </Link>
      {' · '}
      <Link to="/driveline/terms" className="text-teal-700 underline">Terms of Service</Link>
      {' · '}
      <Link to="/get-started" className="text-teal-700 underline" data-testid="pricing-to-signup">Start a free trial</Link>
      {' · '}
      <Link to="/driveline#enquiry" className="text-teal-700 underline" data-testid="pricing-to-enquiry">Talk to us first</Link>
    </p>
  </PolicyPage>
  );
};

export default DrivelinePricing;
